import { zodResolver } from '@hookform/resolvers/zod';
import { backend } from 'modules/data';
import { clearCacheStorage } from 'modules/data-providers';
import { useDispatch, useSelector } from 'modules/react-redux';
import { setCurrencyPrecision } from 'pages/requestForm/containers/netsuite/helpers';
import { useVendorTemplateFieldSettings } from 'pages/requestForm/containers/netsuite/hooks';
import { isFieldVisible } from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { NetSuiteVendorFieldsTransfer } from 'pages/requestForm/containers/netsuite/NetSuiteVendorRequestForm/NetSuiteVendorRequestForm.types';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useCreateNetSuiteVendor } from 'shared/data/webApp/v1';

import { getVendorTransfer } from '../../../NetSuiteVendorForm/NetSuiteVendorForm.helpers';
import { createSchema } from '../../NetSuiteVendorCreationPopupContent.schema';
import { FormValues, VendorType } from '../../NetSuiteVendorCreationPopupContent.types';
import { getFormDefaultValues } from './NetSuiteVendorCreationPopupForm.helpers';
import {
    NetSuiteVendorCreationPopupFormProps,
    UseNetSuiteVendorCreationFormParams,
} from './NetSuiteVendorCreationPopupForm.types';

export const useNetSuiteVendorCreationForm = (props: UseNetSuiteVendorCreationFormParams) => {
    const { currenciesMap, subsidiaries, requestSubsidiary, ocrData, onSave } = props;

    const fieldSettings = useVendorTemplateFieldSettings();
    const dispatch = useDispatch();
    const request = useSelector(requestSelector);

    const defaultSubsidiary = useMemo(() => {
        const subsidiary = subsidiaries.find((item) => item.id === requestSubsidiary?.id);

        return subsidiary || null;
    }, [subsidiaries, requestSubsidiary]);

    const defaultCurrency = useMemo(() => {
        const currency = currenciesMap.get(defaultSubsidiary?.currencyInternalId);

        return currency || null;
    }, [currenciesMap, defaultSubsidiary?.currencyInternalId]);

    const withTaxNumber =
        isFieldVisible(fieldSettings.taxNumber) &&
        defaultSubsidiary?.edition !== backend.NetSuiteSubsidiaryEditionType.CA;

    const form = useForm<FormValues>({
        defaultValues: getFormDefaultValues({
            defaultSubsidiaryId: defaultSubsidiary?.id || null,
            defaultCurrencyId: defaultCurrency?.id || null,
            currenciesMap,
            ocrData,
        }),
        resolver: zodResolver(createSchema(fieldSettings, withTaxNumber)),
    });

    useSubscribeFieldsChanges(form, subsidiaries, currenciesMap);

    const { mutate: createNetSuiteVendor, isLoading: isLoadingCreateNetSuiteVendor } = useCreateNetSuiteVendor();

    const handleSubmit = form.handleSubmit(
        ({
            vendorType,
            categoryId,
            defaultExpenseAccountId,
            defaultPayablesAccountId,
            termsId,
            incotermName,
            primarySubsidiaryId,
            currencyId,
            currenciesIds,
            subsidiaries,
            addressLines,
            ...values
        }) => {
            const primarySubsidiaryItem = subsidiaries?.find((item) => item.subsidiaryId === primarySubsidiaryId);

            const fieldValues: NetSuiteVendorFieldsTransfer = {
                ...values,
                isPerson: vendorType === VendorType.Individual,
                category: categoryId ? { id: categoryId } : null,
                defaultExpenseAccount: defaultExpenseAccountId ? { id: defaultExpenseAccountId } : null,
                defaultPayablesAccount: defaultPayablesAccountId ? { id: defaultPayablesAccountId } : null,
                terms: termsId ? { id: termsId } : null,
                incoterm: incotermName ? { text: incotermName } : null,
                primarySubsidiary: primarySubsidiaryId ? { id: primarySubsidiaryId } : null,
                creditLimit: values.creditLimit ?? primarySubsidiaryItem?.creditLimit ?? null,
                currency: currencyId ? { id: currencyId } : null,
                currencies: currenciesIds?.map((id) => ({ id })),
                subsidiaries: subsidiaries?.map((item) => {
                    let creditLimit = item.creditLimit ?? null;

                    if (item.subsidiaryId === primarySubsidiaryId && creditLimit === null) {
                        creditLimit = values.creditLimit ?? null;
                    }

                    return {
                        creditLimit,
                        subsidiary: item.subsidiaryId ? { id: item.subsidiaryId } : null,
                        taxCode: item.taxCodeId ? { id: item.taxCodeId } : null,
                    };
                }),
                addressLines: addressLines?.map((item) => ({
                    ...item,
                    country: item.countryId ? { id: item.countryId } : null,
                })),
            };

            createNetSuiteVendor(
                {
                    body: {
                        companyId: request.companyId,
                        vendor: getVendorTransfer(fieldValues),
                    },
                },
                {
                    onSuccess: (data) => {
                        if (data.vendor && onSave) {
                            onSave(data.vendor);

                            dispatch(
                                clearCacheStorage(
                                    (storageId) => storageId === `requestOptions.netSuiteVendors.${request.id}`
                                )
                            );
                        }
                    },
                }
            );
        }
    );

    return {
        form,
        handleSubmit,
        isLoadingCreateNetSuiteVendor,
        withTaxNumber,
    };
};

const useSubscribeFieldsChanges = (
    form: UseFormReturn<FormValues>,
    subsidiaries: NetSuiteVendorCreationPopupFormProps['subsidiaries'],
    currenciesMap: UseNetSuiteVendorCreationFormParams['currenciesMap']
) => {
    useEffect(() => {
        const { unsubscribe } = form.watch((value, { name }) => {
            if (!name) {
                return;
            }

            if (name === 'primarySubsidiaryId') {
                const primarySubsidiaryId = value.primarySubsidiaryId;

                const primarySubsidiary = subsidiaries.find((item) => item.id === primarySubsidiaryId);

                const currency = currenciesMap.get(primarySubsidiary?.currencyInternalId);

                form.setValue('currencyId', currency?.id ?? null);
                form.setValue('currenciesIds', currency?.id ? [currency.id] : []);

                return;
            }

            if (name === 'vendorType') {
                form.clearErrors('companyName');

                if (value.vendorType === VendorType.Individual) {
                    form.setValue('webAddress', '');
                } else {
                    form.setValue('salutation', '');
                    form.setValue('firstName', '');
                    form.setValue('middleName', '');
                    form.setValue('lastName', '');
                    form.setValue('jobTitle', '');
                }

                return;
            }

            if (name === 'currencyId') {
                const precision = currenciesMap.get(value.currencyId)?.precision ?? 2;

                form.setValue('creditLimit', setCurrencyPrecision(value.creditLimit, precision));

                return;
            }

            if (name.startsWith('subsidiaries.')) {
                if (name.endsWith('.subsidiaryId')) {
                    const index = Number(name.split('.')[1]);

                    const subsidiary = subsidiaries.find(
                        (item) => item.id === value.subsidiaries?.[index]?.subsidiaryId
                    );
                    const currencyPrecision = currenciesMap.get(subsidiary?.currencyInternalId)?.precision ?? 2;

                    form.setValue(`subsidiaries.${index}.taxCodeId`, null);
                    form.setValue(
                        `subsidiaries.${index}.creditLimit`,
                        setCurrencyPrecision(value.subsidiaries?.[index]?.creditLimit, currencyPrecision)
                    );
                } else if (name.endsWith('.creditLimit')) {
                    form.trigger('creditLimit');
                }

                form.trigger('subsidiaries');

                return;
            }

            if (/^addressLines\.\d+\.defaultBilling$/.test(name)) {
                const index = Number(name.split('.')[1]);

                if (value.addressLines?.[index]?.defaultBilling) {
                    for (let i = 0; i < Number(value.addressLines?.length); i++) {
                        if (i !== index) {
                            form.setValue(`addressLines.${i}.defaultBilling`, false);
                        }
                    }
                }

                return;
            }

            if (/^addressLines\.\d+\.defaultShipping$/.test(name)) {
                const index = Number(name.split('.')[1]);

                if (value.addressLines?.[index]?.defaultShipping) {
                    for (let i = 0; i < Number(value.addressLines?.length); i++) {
                        if (i !== index) {
                            form.setValue(`addressLines.${i}.defaultShipping`, false);
                        }
                    }
                }

                return;
            }
        });

        return () => unsubscribe();
    }, [currenciesMap, form, subsidiaries]);
};
